import React, { useEffect, useRef, useState } from "react";
import type { OperatorSiteSnapshot, SiteId } from "@siteagain/shared-types";

import type { Locale } from "../../i18n";
import { formatSpend } from "../format";
import { siteInitials, siteTone } from "../SiteMark";
import { healthTone, type Mc2Tone } from "./data";
import type { MC2Copy } from "./copy";

export type MCView = "brief" | "inbox" | "fleet" | "pipeline" | "reports" | "pilots";

export interface ShellCounts {
  inbox: number;
  fleet: number;
  pipeline: number;
  reports: number;
}

export function MissionShell({
  view,
  onView,
  counts,
  sites,
  activeWorkflows,
  monthUsageMicroUsd,
  operatorEmail,
  copy,
  locale,
  onOpenSite,
  onExit,
  accountSlot,
  monitoringLabel,
  pageKey,
  children,
}: {
  view: MCView;
  onView(view: MCView): void;
  counts: ShellCounts;
  sites: readonly OperatorSiteSnapshot[];
  activeWorkflows: number;
  monthUsageMicroUsd: number;
  operatorEmail: string;
  copy: MC2Copy;
  locale: Locale;
  onOpenSite(siteId: SiteId): void;
  onExit(): void;
  accountSlot: React.ReactNode;
  monitoringLabel: string;
  pageKey: string;
  children: React.ReactNode;
}) {
  const pins = [...sites]
    .sort((left, right) => {
      const rank = (site: OperatorSiteSnapshot) => site.health === "critical" ? 0 : site.health === "watch" ? 1 : site.health === "building" ? 2 : 3;
      return rank(left) - rank(right) || left.name.localeCompare(right.name);
    })
    .slice(0, 4);

  const nav: Array<{ id: MCView; label: string; key: string; count: number; hot?: boolean }> = [
    { id: "brief", label: copy.views.brief, key: copy.keys.brief, count: 0 },
    { id: "inbox", label: copy.views.inbox, key: copy.keys.inbox, count: counts.inbox, hot: counts.inbox > 0 },
    { id: "fleet", label: copy.views.fleet, key: copy.keys.fleet, count: counts.fleet },
    { id: "pipeline", label: copy.views.pipeline, key: copy.keys.pipeline, count: counts.pipeline },
    { id: "reports", label: copy.views.reports, key: copy.keys.reports, count: counts.reports },
    { id: "pilots", label: copy.views.pilots, key: copy.keys.pilots, count: 0 },
  ];

  // The prototype's keyboard map: G then a letter. Only navigation is bound;
  // there is no fake approve/snooze shortcut for actions that are not reversible.
  const chord = useRef(false);
  const mainRef = useRef<HTMLElement>(null);
  useEffect(() => { mainRef.current?.scrollTo({ top: 0 }); }, [pageKey]);
  useEffect(() => {
    const down = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (target && /^(INPUT|TEXTAREA|SELECT)$/u.test(target.tagName)) return;
      if (event.key.toLowerCase() === "g") { chord.current = true; window.setTimeout(() => { chord.current = false; }, 900); return; }
      if (!chord.current) return;
      const map: Record<string, MCView> = { b: "brief", i: "inbox", f: "fleet", p: "pipeline", r: "reports", a: "pilots" };
      const next = map[event.key.toLowerCase()];
      if (!next) return;
      event.preventDefault();
      chord.current = false;
      onView(next);
    };
    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, [onView]);

  return (
    <div className="mc2">
      <nav className="mc2-rail" aria-label="Mission Control">
        <div className="mc2-brand">
          <span className="mc2-logo" aria-hidden="true">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="#151513" strokeWidth="1.6">
              <rect x="2" y="5" width="9" height="9" rx="1.5" />
              <path d="M5 5V3.5A1.5 1.5 0 0 1 6.5 2h6A1.5 1.5 0 0 1 14 3.5v6a1.5 1.5 0 0 1-1.5 1.5H11" />
            </svg>
          </span>
          <span>
            <span className="mc2-brandname">SiteAgain</span>
            <span className="mc2-brandsub">{copy.rail.mission}</span>
          </span>
        </div>

        {nav.map((item) => (
          <button
            key={item.id}
            type="button"
            className={`mc2-nav${view === item.id ? " on" : ""}`}
            aria-current={view === item.id ? "page" : undefined}
            onClick={() => onView(item.id)}
          >
            <span className="lbl">{item.label}</span>
            <span className="key">{item.key}</span>
            {item.count > 0 && (
              <span className={`cnt${item.hot ? " hot" : ""}`}>{item.count}</span>
            )}
          </button>
        ))}

        {pins.length > 0 && (
          <>
            <div className="mc2-railsec">{copy.rail.pinned}</div>
            {pins.map((site) => {
              const health = healthTone(site);
              const waiting = site.awaitingApproval + site.pendingRecommendations;
              return (
                <button key={site.id} type="button" className="mc2-pin" onClick={() => onOpenSite(site.id)}>
                  <span className={`mc2-dot ${health.dot}`} />
                  <span className="name">{site.name}</span>
                  <span className="pd">{waiting > 0 ? copy.rail.toDo(waiting) : health.label}</span>
                </button>
              );
            })}
          </>
        )}

        <div className="mc2-railfoot">
          <div style={{ display: "flex", alignItems: "center", gap: 8, color: "#edeae2" }}>
            <span className={`mc2-dot ${activeWorkflows > 0 ? "ok" : "idle"}`} />
            {monitoringLabel}
          </div>
          <div className="spend">{copy.rail.spend} <span className="mc2-mono" style={{ color: "#edeae2" }}>{formatSpend(monthUsageMicroUsd)}</span></div>
          <div className="account">
            <span className="who" title={operatorEmail}>{operatorEmail}</span>
            {accountSlot}
          </div>
          <button type="button" className="account" onClick={onExit}>{copy.rail.exit} →</button>
          <span className="mc2-mono" style={{ fontSize: 10, color: "#6e6a61" }}>{locale.toUpperCase()}</span>
        </div>
      </nav>

      <main className="mc2-main" ref={mainRef}>{children}</main>
    </div>
  );
}

/** Search across the sites the operator can open. Real results only; no invented commands. */
export function TopSearch({
  sites,
  placeholder,
  onOpenSite,
  value,
  onChange,
  ariaLabel,
}: {
  sites: readonly OperatorSiteSnapshot[];
  placeholder: string;
  onOpenSite(siteId: SiteId): void;
  value?: string;
  onChange?(value: string): void;
  ariaLabel: string;
}) {
  const [local, setLocal] = useState("");
  const [open, setOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const query = value ?? local;
  const setQuery = onChange ?? setLocal;
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        inputRef.current?.focus();
      }
      if (event.key === "Escape" && document.activeElement === inputRef.current) inputRef.current?.blur();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  const results = query.trim()
    ? sites.filter((site) => [site.name, site.slug, site.productionDomain, site.ownerId]
        .some((part) => part?.toLowerCase().includes(query.trim().toLowerCase()))).slice(0, 6)
    : [];
  return (
    <label className="mc2-search">
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
        <circle cx="7" cy="7" r="4.5" /><path d="M10.5 10.5 14 14" />
      </svg>
      <input
        ref={inputRef}
        type="search"
        value={query}
        placeholder={placeholder}
        aria-label={ariaLabel}
        onChange={(event) => { setQuery(event.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => window.setTimeout(() => setOpen(false), 150)}
      />
      <span className="mc2-kbd">⌘K</span>
      {open && results.length > 0 && (
        <ul className="mc2-search-results">
          {results.map((site) => (
            <li key={site.id}>
              <button
                type="button"
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => { setQuery(""); setOpen(false); onOpenSite(site.id); }}
              >
                <span className="mc2-av" style={{ background: siteTone(site.id), width: 24, height: 24, fontSize: 10 }}>{siteInitials(site.name)}</span>
                <span>{site.name}</span>
                <span className="mc2-muted mc2-mono" style={{ marginLeft: "auto", fontSize: 11 }}>{site.productionDomain ?? site.slug}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </label>
  );
}

export function StatusDot({ tone }: { tone: Mc2Tone }) {
  const color = tone === "up" ? "var(--mc-ok)" : tone === "down" ? "var(--mc-error)" : tone === "warn" ? "var(--mc-wait-fill)" : "var(--mc-muted)";
  return <span aria-hidden="true" style={{ width: 8, height: 8, borderRadius: 99, background: color, display: "inline-block", flex: "none" }} />;
}
